import { CrudFilters, IResourceComponentsProps, useShow, useNavigation, usePermissions, useDelete } from "@pankod/refine-core";
import {
    List,
    Table,
    TextField,
    useTable,
    getDefaultSortOrder,
    Space,
    EditButton,
    DeleteButton,
    ShowButton,
    Typography,
    Form,
    Icons,
    Col,
    Row,
    Button,
    Input,
    Tooltip,
    CreateButton,
    notification,
    Tag,
    Modal,
    Checkbox,
} from "@pankod/refine-antd";
import { EyeOutlined, CheckOutlined, CalendarOutlined, DollarOutlined } from "@ant-design/icons";
import { IRentPlace, RentPlaceStatusEnum } from "../../interfaces/rent-place.interface";
import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import 'dayjs/locale/es';
import { rentPlaceStatusOptions, statusColors, placeColors, hasPlacePermission, getAllowedPlaces, placeOptions } from "../../constants/rentPlace.constants";
import { RentPlaceDetailModal } from "../../components/molecules/rent-place-detail-modal";
import { RentPlacePaymentModal } from "../../components/molecules/rent-place-payment-modal";
import { axiosWithAuth } from "../../utils/axiosWithAuth";
import { API_URL, TOKEN_KEY } from "../../constants/environment";
import { useLocation } from "@pankod/refine-react-router-v6";

const { Text } = Typography;

// Configurar dayjs en español
dayjs.locale('es');
export const RentPlaceList: React.FC<IResourceComponentsProps> = () => {
    const { push } = useNavigation();
    const { data: permissionsData } = usePermissions();
    const { mutate: deleteRecord } = useDelete();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const hasRedirectedRef = useRef(false);
    const location = useLocation();
    
    const [visible, setVisible] = useState(false);
    const [selectedRentPlace, setSelectedRentPlace] = useState<IRentPlace | undefined>();
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [selectedPaymentRecord, setSelectedPaymentRecord] = useState<IRentPlace | null>(null);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [selectedConfirmRecord, setSelectedConfirmRecord] = useState<IRentPlace | null>(null);
    const [confirmationObservations, setConfirmationObservations] = useState("");
    
    // Cargar estado inicial del localStorage o usar true por defecto
    const [hideCanceled, setHideCanceled] = useState(() => {
        const saved = localStorage.getItem('rent-places-hide-canceled');
        return saved !== null ? JSON.parse(saved) : true;
    });

    // Función para manejar el cambio del checkbox y guardar en localStorage
    const handleHideCanceledChange = (checked: boolean) => {
        setHideCanceled(checked);
        localStorage.setItem('rent-places-hide-canceled', JSON.stringify(checked));
    };

    // Redirigir automáticamente al calendario en dispositivos móviles (solo al cargar inicialmente)
    useEffect(() => {
        // No redirigir si viene desde el calendario (state.fromCalendar) o si ya se redirigió
        const comesFromCalendar = (location.state as any)?.fromCalendar;
        
        if (isMobile && !hasRedirectedRef.current && !comesFromCalendar) {
            hasRedirectedRef.current = true;
            push('/rent-places/calendar');
        }
    }, [isMobile, push, location.state]);
    
    const { tableProps, sorter, searchFormProps } = useTable<IRentPlace>({
        initialSorter: [
            {
                field: "id",
                order: "asc",
            },
        ],
        initialPageSize: 15,
        onSearch: (params: any) => {
            const filters: CrudFilters = [];
            const { q } = params;
            filters.push({
                field: "search",
                operator: "eq",
                value: q,
            });
            return filters;
        },
        permanentFilter: [
            {
                field: "hide_canceled",
                operator: "eq",
                value: hideCanceled
            }
        ],
        queryOptions: {
            queryKey: ['rent-places-list', hideCanceled]
        }
    });
    
    const { queryResult, showId, setShowId } = useShow<IRentPlace>();
    const { data, isLoading } = queryResult;
    const record = data?.data;

    const isAdmin = permissionsData?.includes('rent-places:admin');
    const allowedPlaces = getAllowedPlaces(permissionsData || []);
    const hasAnyPlacePermission = allowedPlaces.length > 0;

    // Filtrar las opciones de lugares según los permisos del usuario
    const filteredPlaceOptions = placeOptions.filter(option => 
        isAdmin || allowedPlaces.includes(option.value)
    );

    const calculateTimeRemaining = (startDate: string, status: RentPlaceStatusEnum, isFree: boolean = false) => {
        const today = dayjs().startOf('day');
        const eventDate = dayjs(startDate).startOf('day');
        
        if (eventDate.isBefore(today)) {
            return { text: "Evento pasado", color: "gray", isPast: true };
        }
        
        const diffInDays = eventDate.diff(today, 'days');
        
        const months = Math.floor(diffInDays / 30);
        const remainingDays = diffInDays % 30;
        
        let timeText = "";
        if (months > 0) {
            timeText += `${months} ${months === 1 ? 'mes' : 'meses'}`;
            if (remainingDays > 0) {
                timeText += ` ${remainingDays} ${remainingDays === 1 ? 'día' : 'días'}`;
            }
        } else {
            timeText = `${diffInDays} ${diffInDays === 1 ? 'día' : 'días'}`;
        }
        
        // Nueva lógica de colores
        const isPaid = status === RentPlaceStatusEnum.PAID;
        const isConfirmed = status === RentPlaceStatusEnum.CONFIRMED;
        
        let color = "green"; // Por defecto verde
        
        // Si faltan 30 días o menos y el evento no está confirmado o pagado, debe salir color naranja
        if (diffInDays <= 30 && !isConfirmed && !isPaid) {
            color = "orange";
        }
        
        // Si faltan 15 días o menos y el evento no está pagado y no es gratuito o no está confirmado y es gratuito, debe ser rojo
        if (diffInDays <= 15) {
            if ((!isPaid && !isFree) || (!isConfirmed && isFree)) {
                color = "red";
            }
        }

        if (eventDate.isSame(today)) {
            timeText = 'Hoy';
        }
        
        return {
            text: timeText,
            color: color,
            isPast: false
        };
    };

    const formatDateTimeRange = (record: IRentPlace) => {
        const startDate = dayjs(record.start_date);
        const endDate = dayjs(record.end_date);
        const isSameDay = startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD');
        
        let dateText = startDate.format('DD MMM YYYY');
        if (!isSameDay) {
            dateText += ` - ${endDate.format('DD MMM YYYY')}`;
        }
        
        let timeText = "";
        if (record.start_time) {
            timeText = record.start_time;
            if (record.end_time) {
                timeText += ` - ${record.end_time}`;
            }
        }
        
        return { dateText, timeText };
    };

    const handlePayment = (record: IRentPlace) => {
        setSelectedPaymentRecord(record);
        setPaymentModalVisible(true);
    };

    const handlePaymentSuccess = () => {
        setPaymentModalVisible(false);
        setSelectedPaymentRecord(null);
        // Refrescar la tabla
        window.location.reload();
    };

    const handleConfirm = (record: IRentPlace) => {
        setSelectedConfirmRecord(record);
        setConfirmationObservations(record.observations || "");
        setConfirmModalVisible(true);
    };

    const handleConfirmSubmit = async () => {
        if (!selectedConfirmRecord) return;

        const token = localStorage.getItem(TOKEN_KEY);
        try {
            await axiosWithAuth().patch(`${API_URL}/rent-places/${selectedConfirmRecord.id}/confirm`, {
                observations: confirmationObservations
            }, {
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });

            notification.success({
                message: "Evento confirmado exitosamente",
                description: "El evento ha sido confirmado correctamente.",
            });

            setConfirmModalVisible(false);
            setSelectedConfirmRecord(null);
            setConfirmationObservations("");
            // Refrescar la tabla
            window.location.reload();
        } catch (error: any) {
            notification.error({
                message: "Error al confirmar evento",
                description: error?.response?.data?.message || "Ocurrió un error al confirmar el evento.",
            });
        }
    };

    const handleConfirmCancel = () => {
        setConfirmModalVisible(false);
        setSelectedConfirmRecord(null);
        setConfirmationObservations("");
    };

    const handleCustomDelete = (record: IRentPlace) => {
        const today = dayjs().format('YYYY-MM-DD');
        const eventCreationDate = dayjs(record.created_at).format('YYYY-MM-DD'); // Asumiendo que usamos start_date como referencia de creación
        console.log(today, eventCreationDate);  
        const isCreatedToday = today === eventCreationDate;
        
        const title = isCreatedToday ? "¿Eliminar evento?" : "¿Cancelar evento?";
        const content = isCreatedToday 
            ? "¿Estás seguro de que deseas eliminar este evento? Esta acción no se puede deshacer."
            : "Este evento será cancelado. ¿Estás seguro de que deseas continuar?";

        Modal.confirm({
            title: title,
            content: content,
            okText: isCreatedToday ? "Sí, eliminar" : "Sí, cancelar",
            okType: "danger",
            cancelText: "No, mantener",
            maskClosable: true,
            onOk: () => {
                deleteRecord({
                    resource: "rent-places",
                    id: record.id || "",
                }, {
                    onSuccess: () => {
                        notification.success({
                            message: isCreatedToday ? "Evento eliminado" : "Evento cancelado",
                            description: isCreatedToday 
                                ? "El evento ha sido eliminado exitosamente."
                                : "El evento ha sido cancelado exitosamente.",
                        });
                    },
                    onError: (error) => {
                        notification.error({
                            message: "Error",
                            description: `Ocurrió un error al ${isCreatedToday ? 'eliminar' : 'cancelar'} el evento.`,
                        });
                    }
                });
            },
        });
    };

    return (
        <>
            <List
                headerButtons={({ defaultButtons }) => ([
                    ...(hasAnyPlacePermission ? [<CreateButton key="create" children="Nueva renta de lugar"/>] : []),
                    <Button 
                        key="calendarView" 
                        type="default" 
                        icon={<CalendarOutlined />}
                        onClick={() => push('/rent-places/calendar')}
                    >
                        Vista Calendario
                    </Button>
                ])}
                title={"Renta de Lugares"}
            >
                <Form layout="vertical" {...searchFormProps}>
                    <Row gutter={[20, 0]} align="middle">
                        <Col span={12}>
                            <Form.Item name="q">
                                <Input
                                    placeholder="Buscar renta de lugar"
                                    prefix={<Icons.SearchOutlined />}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item>
                                <Space>
                                    <Button htmlType="submit" type="primary">
                                        Buscar
                                    </Button>
                                    <Button 
                                        onClick={() => {
                                            searchFormProps.form?.resetFields();
                                            searchFormProps.form?.submit();
                                        }}
                                    >
                                        Limpiar
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item>
                                <Checkbox 
                                    checked={hideCanceled}
                                    onChange={(e) => handleHideCanceledChange(e.target.checked)}
                                >
                                    Ocultar cancelados
                                </Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <Table {...tableProps} rowKey="id">
                    <Table.Column
                        dataIndex="id"
                        key="id"
                        title="ID"
                        render={(value) => <TextField value={value} />}
                        defaultSortOrder={getDefaultSortOrder("id", sorter)}
                        sorter
                    />
                    <Table.Column<IRentPlace>
                        dataIndex="start_date"
                        key="start_date"
                        title="Fecha y Hora"
                        render={(_, record) => {
                            const { dateText, timeText } = formatDateTimeRange(record);
                            return (
                                <div>
                                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                        {dateText}
                                    </div>
                                    {timeText && (
                                        <div style={{ fontSize: '11px', color: '#666' }}>
                                            {timeText}
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                        sorter={(a: IRentPlace, b: IRentPlace) => {
                            const dateA = dayjs(`${a.start_date} ${a.start_time || '00:00'}`).valueOf();
                            const dateB = dayjs(`${b.start_date} ${b.start_time || '00:00'}`).valueOf();
                            return dateA - dateB;
                        }}
                        defaultSortOrder={getDefaultSortOrder("start_date", sorter)}
                        width={130}
                    />
                    <Table.Column
                        dataIndex="place"
                        key="place"
                        title="Lugar"
                        render={(value) => (
                            <Tag
                                color={placeColors[value] || '#1890ff'}
                                style={{ 
                                    color: 'white', 
                                    fontWeight: 'bold',
                                    border: 'none'
                                }}
                            >
                                {value}
                            </Tag>
                        )}
                        sorter
                    />
                    <Table.Column
                        dataIndex="receipt_number"
                        key="receipt_number"
                        title="No. Recibo"
                        render={(value) => <TextField value={value || "--"} />}
                        sorter
                        width={120}
                    />
                    <Table.Column
                        dataIndex="institution"
                        key="institution"
                        title="Institución"
                        render={(value) => <TextField value={value || "--"} />}
                        sorter
                        width={150}
                    />
                    <Table.Column
                        dataIndex="event_name"
                        key="event_name"
                        title="Nombre del Evento"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="responsible_name"
                        key="responsible_name"
                        title="Responsable"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="responsible_phone"
                        key="responsible_phone"
                        title="Celular"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column<IRentPlace>
                        dataIndex="start_date"
                        key="start_date"
                        title="Tiempo Restante"
                        render={(_, record) => {
                            const timeInfo = calculateTimeRemaining(
                                record.start_date || '', 
                                record.status,
                                record.is_free || false
                            );
                            
                            // Mapear colores a códigos hex
                            const colorMap = {
                                gray: '#d9d9d9',
                                blue: '#1890ff',
                                green: '#52c41a',
                                orange: '#faad14',
                                red: '#ff4d4f'
                            };
                            
                            return (
                                <Tag
                                    color={colorMap[timeInfo.color as keyof typeof colorMap]}
                                    style={{ 
                                        color: timeInfo.isPast ? '#666' : 'white',
                                        fontWeight: 'bold',
                                        border: 'none'
                                    }}
                                >
                                    {timeInfo.text}
                                </Tag>
                            );
                        }}
                        sorter
                    />
                    <Table.Column
                        dataIndex="is_free"
                        key="is_free"
                        title="Tipo"
                        render={(value) => (
                            <Tag
                                color={value ? "#52c41a" : "#faad14"}
                                style={{ 
                                    color: 'white', 
                                    fontWeight: 'bold',
                                    border: 'none'
                                }}
                            >
                                {value ? "Gratuito" : "Con Costo"}
                            </Tag>
                        )}
                    />
                    <Table.Column
                        dataIndex="observations"
                        key="observations"
                        title="Observaciones"
                        render={(value) => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="status"
                        key="status"
                        title="Estatus"
                        render={(value: RentPlaceStatusEnum) => (
                            <Tag
                                color={statusColors[value] || '#1890ff'}
                                style={{ 
                                    color: 'white', 
                                    fontWeight: 'bold',
                                    border: 'none'
                                }}
                            >
                                {rentPlaceStatusOptions[value] || value}
                            </Tag>
                        )}
                        sorter  
                    />
                    { isAdmin && (
                        <Table.Column
                            dataIndex="needs_invoice"
                            key="needs_invoice"
                            title="Factura"
                            render={(value: boolean, record: IRentPlace) => {
                                if (record.status !== "PAID") return "--";
                                return (
                                    <Tag color={value ? 'green' : 'red'} 
                                        style={{ 
                                            fontWeight: 'bold',
                                            border: 'none'
                                        }}>
                                        {value ? 'Si' : 'No'}
                                    </Tag>
                                );
                            }}
                            width={80}
                            sorter
                        />
                    )}
                    <Table.Column<IRentPlace>
                        title="Acciones"
                        dataIndex="actions"
                        render={(_, record) => {
                            const canManageRecord = isAdmin || hasPlacePermission(record.place || '', permissionsData || []);
                            
                            // Verificar si el evento ya pasó
                            const today = dayjs().startOf('day');
                            const eventDate = dayjs(record.start_date).startOf('day');
                            const isEventPast = eventDate.isBefore(today);
                            
                            const canEdit = canManageRecord && [RentPlaceStatusEnum.RESERVED, RentPlaceStatusEnum.CONFIRMED, RentPlaceStatusEnum.WAITING_LIST].includes(record.status) && !isEventPast;
                            const canDelete = canManageRecord && [RentPlaceStatusEnum.RESERVED, RentPlaceStatusEnum.CONFIRMED, RentPlaceStatusEnum.WAITING_LIST].includes(record.status) && !isEventPast;
                            const canPay = isAdmin && [RentPlaceStatusEnum.RESERVED, RentPlaceStatusEnum.CONFIRMED].includes(record.status) && !record.is_free && !isEventPast;
                            const canConfirm = record.status === RentPlaceStatusEnum.RESERVED && !isEventPast;
                            
                            return (
                                <Space>
                                    {(isAdmin || canEdit) && (
                                        <EditButton hideText size="small" recordItemId={record.id} />
                                    )}
                                    <ShowButton
                                        hideText
                                        size="small"
                                        recordItemId={record.id}
                                        onClick={() => {
                                            setShowId(record.id);
                                            setVisible(true);
                                        }}
                                    />
                                    {canConfirm && (
                                        <Tooltip title="Confirmar Evento">
                                            <Button
                                                size="small"
                                                type="primary"
                                                icon={<CheckOutlined />}
                                                onClick={() => handleConfirm(record)}
                                                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                                            />
                                        </Tooltip>
                                    )}
                                    {canPay && (
                                        <Tooltip title="Procesar Pago">
                                            <Button
                                                size="small"
                                                type="primary"
                                                icon={<DollarOutlined />}
                                                onClick={() => handlePayment(record)}
                                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                            />
                                        </Tooltip>
                                    )}
                                    {canDelete && (
                                        <Tooltip title="Eliminar">
                                            <Button
                                                size="small"
                                                danger
                                                icon={<Icons.DeleteOutlined />}
                                                onClick={() => handleCustomDelete(record)}
                                            />
                                        </Tooltip>
                                    )}
                                </Space>
                            );
                        }}
                    />
                </Table>
            </List>
            <RentPlaceDetailModal
                visible={visible}
                onCancel={() => setVisible(false)}
                onOk={() => setVisible(false)}
                isLoading={isLoading}
                showId={showId || ""}
                showRecord={record}
                hasEditPermission={isAdmin}
            />
            <RentPlacePaymentModal
                visible={paymentModalVisible}
                onCancel={() => {
                    setPaymentModalVisible(false);
                    setSelectedPaymentRecord(null);
                }}
                onOk={handlePaymentSuccess}
                recordId={selectedPaymentRecord?.id || ""}
                eventName={selectedPaymentRecord?.event_name}
                existingPayment={{
                    rental_cost: selectedPaymentRecord?.rental_cost,
                    rental_discount: selectedPaymentRecord?.rental_discount,
                    rental_total: selectedPaymentRecord?.rental_total,
                    payment_comment: selectedPaymentRecord?.payment_comment,
                    needs_invoice: selectedPaymentRecord?.needs_invoice,
                }}
            />
            <Modal
                title="Confirmar Evento"
                visible={confirmModalVisible}
                onOk={handleConfirmSubmit}
                onCancel={handleConfirmCancel}
                okText="Confirmar"
                cancelText="Cancelar"
                destroyOnClose
                width={500}
            >
                <div style={{ marginBottom: 24 }}>
                    <p><strong>Evento:</strong> {selectedConfirmRecord?.event_name}</p>
                    <p><strong>Lugar:</strong> {selectedConfirmRecord?.place}</p>
                    <p><strong>Responsable:</strong> {selectedConfirmRecord?.responsible_name}</p>
                </div>
                
                <div style={{ marginBottom: 16 }}>
                    <label style={{ 
                        display: 'block', 
                        marginBottom: 8, 
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}>
                        Observaciones de Confirmación:
                    </label>
                    <Input.TextArea
                        rows={4}
                        value={confirmationObservations}
                        onChange={(e) => setConfirmationObservations(e.target.value)}
                        placeholder="Agregar observaciones sobre la confirmación del evento..."
                        maxLength={500}
                        showCount
                        style={{ resize: 'none' }}
                    />
                </div>
            </Modal>
        </>
    );
};

export default RentPlaceList;
